import { graphql } from '~/graphql';
import type {
  GetBuyerOrdersWithChatsQuery,
  GetBuyerOrdersWithChatsQueryVariables,
  GetSellerOrdersWithChatsQuery,
  TradePerspective,
} from '~/graphql/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';

const GetChatsByOrderDocument = graphql(`
  query GetChatsByOrder($orderId: ID!) {
    chats(orderId: $orderId) {
      id
      createdAt
      type
      title
      archived
      order {
        id
        purchaseOrderNumber
        cancellationNote
        shippingLocation {
          name
          facility {
            address {
              city
              region
            }
          }
        }
      }
      orderReference {
        order {
          id
          purchaseOrderNumber
          status
          cancellationNote
          currency
          items {
            platformProductsLengths {
              isNominal
              platformProduct {
                name
                actualWidthInches
                actualThicknessInches
                nominalWidthInches
                nominalThicknessInches
                actualLengthInches
                piecesPerBundle {
                  value
                }
              }
              lengthDisplayValue
              length {
                actualLengthInches
                nominalLengthInches
              }
            }
            quantity(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            quantityStored: quantity(mode: { mode: STORED }) {
              value
            }
            effectivePrice {
              amount
              currency
            }
            price {
              amount
              currency
            }
            suggestedPrice {
              amount
              currency
            }
          }
        }
      }
      sellingOrganization {
        id
        name
        sellingOrganizationCode
      }
      buyingOrganization {
        id
        name
      }
      email {
        subject
        htmlBody
        recipientTo
        status
      }
    }
  }
`);

const CHAT_REFETCH_INTERVAL_MS = 5_000;

export const useGetChatsByOrder = (orderId: string | null) =>
  useGraphqlQuery(
    GetChatsByOrderDocument,
    ['chats', orderId ?? ''],
    { orderId: orderId! },
    {
      enabled: !!orderId,
      refetchInterval: orderId ? CHAT_REFETCH_INTERVAL_MS : false,
    }
  );

const GetBuyerOrdersWithChatsDocument = graphql(`
  query GetBuyerOrdersWithChats {
    buyerOrdersWithChats {
      id
      purchaseOrderNumber
      sellingOrganization {
        name
        sellingOrganizationCode
      }
      buyingOrganization {
        name
      }
    }
  }
`);

const GetSellerOrdersWithChatsDocument = graphql(`
  query GetSellerOrdersWithChats {
    sellerOrdersWithChats {
      id
      sellingOrganization {
        name
      }
      buyingOrganization {
        name
      }
    }
  }
`);

export const useGetOrdersWithChats = (perspective: TradePerspective) =>
  useGraphqlQuery<
    GetBuyerOrdersWithChatsQuery | GetSellerOrdersWithChatsQuery,
    GetBuyerOrdersWithChatsQueryVariables
  >(
    perspective === 'SELLING'
      ? GetSellerOrdersWithChatsDocument
      : GetBuyerOrdersWithChatsDocument,
    ['ordersWithChats', perspective]
  );
